import { useState } from "react";
import { Modal, Meter } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, useRateCard, STUDIO } from "../lib/store";
import { readPaymentStructure, scheduleFromRead, scheduleFromStages, billingTotals, paymentLinkUrl, paymentLinkText, invoiceReceived, valuationTotals, monthlyDraw, feeDiscount, scheduleIsStale, scheduleUntouched } from "../lib/billing";
import { computeEstimate, defaultConfig } from "../lib/rates";


import { openWhatsApp } from "../lib/whatsapp";
import { ShareStanding } from "../components/ShareStanding";
import type { PaymentMilestone, StageValuation } from "../lib/types";
import { fmtDate, daysUntil } from "../lib/format";
import type { Project } from "../lib/types";

/* The payment structure a project is billed under, and the invoices raised
   against it. Read it off the signed contract, or build it from the fee
   stages when there's no contract to read. */

/** Contracts arrive as PDFs — read them raw, not through the image pipeline. */
function rawDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("unreadable"));
    r.readAsDataURL(file);
  });
}

export default function BillingSchedule({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const rateCard = useRateCard();
  const [reading, setReading] = useState(false);
  const [readOpen, setReadOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<string | null>(null);
  const b = p.billing;
  const totals = billingTotals(p);

  if (!b) {
    return (
      <>
        <div className="card pad reveal" style={{ maxWidth: 720 }}>
          <div className="kicker">No payment schedule yet</div>
          <h3 className="serif" style={{ fontSize: 24, marginTop: 6 }}>How is this project billed?</h3>
          <p className="muted" style={{ fontSize: 14.5, marginTop: 8, lineHeight: 1.6 }}>
            For a project already under way, read the structure straight off the signed contract — the stages, the
            percentages, retention and payment terms. If there's no contract to hand, build it from the fee stages
            already configured here and adjust.
          </p>
          {canEdit && (
            <div className="row wrap" style={{ gap: 10, marginTop: 18 }}>
              <button className="btn accent" onClick={() => setReadOpen(true)}><Icon.spark width={15} />Read from the contract</button>
              <button className="btn soft" onClick={() => actions.setPaymentSchedule(p.id, scheduleFromStages(p, rateCard))}>
                <Icon.layers width={15} />Build from the fee stages
              </button>
            </div>
          )}
        </div>
        {readOpen && <ReadContractModal p={p} onClose={() => setReadOpen(false)} busy={reading} setBusy={setReading} />}
      </>
    );
  }

  const pctAllocated = b.contractSum > 0 ? Math.min(1, (totals?.allocated ?? 0) / b.contractSum) : 0;
  // a stage valuation takes over the billing; the milestone list stays on record
  const superseded = b.billingMode === "valuation" && (b.valuations ?? []).length > 0;
  // the allocation check only means anything while milestones are what bill
  const mismatch = !superseded && Math.abs((totals?.unallocated ?? 0)) > 1;

  return (
    <div className="col" style={{ gap: 18 }}>
      {/* the structure at a glance */}
      <div className="card pad reveal">
        <div className="row between wrap" style={{ gap: 12 }}>
          <div>
            <div className="kicker">Payment structure</div>
            <div className="row wrap" style={{ gap: 8, marginTop: 6, alignItems: "baseline" }}>
              <span className="serif" style={{ fontSize: 30 }}>{money(b.contractSum, b.currency)}</span>
              <span className="badge">{b.source === "contract" ? "From the contract" : b.source === "stages" ? "From the fee stages" : "Entered by hand"}</span>
              {b.issuedOn ? <span className="badge ok"><span className="dot" />Issued to the client</span> : <span className="badge warn"><span className="dot" />Not yet issued</span>}
            </div>
            {b.sourceNote && <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{b.sourceNote}</div>}
          </div>
          {canEdit && (
            <div className="row wrap" style={{ gap: 8 }}>
              <button className="btn ghost sm" onClick={() => setReadOpen(true)}><Icon.spark width={14} />Re-read a contract</button>
              <button className="btn accent sm" onClick={() => actions.issueSchedule(p.id)}><Icon.send width={14} />{b.issuedOn ? "Re-issue" : "Issue to client"}</button>
            </div>
          )}
        </div>

        {/* how the fee was arrived at — only shown when it's a % of build cost */}
        {b.feeBasis === "percentage-of-construction" && b.constructionBudget && (
          <div className="panel" style={{ padding: "10px 13px", marginTop: 14, fontSize: 12.5 }}>
            <b>{b.feePct ? `${(b.feePct * 100).toFixed(b.feePct * 100 % 1 ? 1 : 0)}%` : "A percentage"}</b> of a{" "}
            {money(b.constructionBudget, b.currency)} construction cost
            {b.standardPct ? <span className="dim"> · against a standard {(b.standardPct * 100).toFixed(0)}%</span> : null}
            {b.servicePeriodMonths ? <span className="dim"> · {b.servicePeriodMonths}-month service period</span> : null}
          </div>
        )}

        {/* a stage-built schedule that no longer matches the fee */}
        {scheduleIsStale(p, rateCard) && (() => {
          const est = computeEstimate(p.estimate ?? defaultConfig(p.budget, p.currency), rateCard);
          const untouched = scheduleUntouched(b);
          return (
            <div className="panel row between wrap" style={{ padding: "10px 13px", marginTop: 10, gap: 12, fontSize: 12.5, borderLeft: "3px solid var(--warn)", background: "color-mix(in srgb, var(--warn) 7%, var(--card))" }}>
              <span>
                <b>The fee has moved.</b> This schedule still totals {money(b.contractSum, b.currency)}; at the current budget and scope the fee is <b>{money(Math.round(est.total), b.currency)}</b>.
                <span className="dim" style={{ display: "block", fontSize: 11.5 }}>{untouched ? "Nothing has been invoiced yet, so it can be rebuilt in place." : "Milestones already billed are kept exactly as invoiced; only the unbilled ones move."}</span>
              </span>
              {canEdit && <button className="btn accent sm" onClick={() => actions.rebuildStageSchedule(p.id, STUDIO.principal)}><Icon.swap width={13} />Rebuild from the current fee</button>}
            </div>
          );
        })()}

        {/* the fee before discount — the client sees it beside the agreed fee */}
        {(() => {
          const d = feeDiscount(b);
          return (
            <div className="panel row between wrap" style={{ padding: "10px 13px", marginTop: 10, gap: 12, fontSize: 12.5 }}>
              <span>
                <b>Fee before discount</b>
                <span className="dim" style={{ display: "block", fontSize: 11.5 }}>
                  {d ? `The client sees ${money(d.full, b.currency)} struck through, ${d.pct.toFixed(d.pct % 1 ? 1 : 0)}% off, saving ${money(d.saving, b.currency)}.` : "Enter the undiscounted fee and the client is shown the full amount and the percentage taken off."}
                </span>
              </span>
              <span className="row" style={{ gap: 6 }}>
                <span className="dim mono" style={{ fontSize: 12 }}>{b.currency}</span>
                <input className="input mono" type="number" min={0} disabled={!canEdit} defaultValue={b.fullFee ?? (d?.full || "")} placeholder="—"
                  onBlur={(e) => actions.setBillingFullFee(p.id, Number(e.target.value) || 0)} style={{ width: 130, textAlign: "right", padding: "6px 9px" }} />
              </span>
            </div>
          );
        })()}

        {/* deferred money — not retention, and it changes the cash flow materially.
            Some contracts (e.g. Garden Park) have none — the studio can clear it. */}
        {b.deferred && (
          <div className="panel" style={{ padding: "11px 14px", marginTop: 12, background: "color-mix(in srgb, var(--warn) 8%, var(--card))", borderColor: "color-mix(in srgb, var(--warn) 35%, var(--line))" }}>
            <div className="row between wrap" style={{ gap: 8 }}>
              <span className="row" style={{ gap: 8 }}>
                <Icon.bell width={14} style={{ color: "var(--warn)" }} />
                <b style={{ fontSize: 13 }}>{(b.deferred.pct * 100).toFixed(0)}% deferred — {money(Math.round(b.contractSum * b.deferred.pct), b.currency)}</b>
              </span>
              <span className="row" style={{ gap: 8 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>not ordinary retention</span>
                {canEdit && <button className="chip" style={{ fontSize: 10.5 }} title="This contract has no deferred payment — remove it" onClick={() => { if (confirm("Remove the deferred-payment clause? Use this when the contract has none.")) actions.clearDeferred(p.id); }}>Remove</button>}
              </span>
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 5, lineHeight: 1.5 }}>
              Payable {b.deferred.trigger}{b.deferred.note ? ` · ${b.deferred.note}` : ""}
            </div>
          </div>
        )}

        {/* advance retainer — an up-front slice of the fee (default 15%) */}
        <RetainerPanel p={p} canEdit={canEdit} />

        {/* online payment link — a hosted checkout the client pays through */}
        <PayLinkRow p={p} canEdit={canEdit} />

        {/* a second reporting currency, and charges that invoice themselves */}
        <FxRow p={p} canEdit={canEdit} />
        <RetainersRow p={p} canEdit={canEdit} />

        {/* the fee split by discipline, where the engagement apportions it */}
        {(b.disciplines ?? []).length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="kicker" style={{ fontSize: 8.5, marginBottom: 6 }}>Apportioned by discipline</div>
            {b.disciplines!.map((d, i) => (
              <div key={i} className="row between" style={{ padding: "4px 0", gap: 10 }}>
                <span style={{ fontSize: 12.5 }}>{d.name}{d.pct ? <span className="dim"> · {(d.pct * 100).toFixed(d.pct * 100 % 1 ? 1 : 0)}%</span> : null}</span>
                <span className="mono" style={{ fontSize: 12 }}>{money(d.amount, b.currency)}</span>
              </div>
            ))}
          </div>
        )}

        {(b.stages ?? []).length > 0 && (
          <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
            {b.stages!.map((s, i) => (
              <span key={i} className="badge" style={{ fontSize: 10 }}>
                {s.label}{s.pct ? ` ${(s.pct * 100).toFixed(0)}%` : ""} · {money(s.amount, b.currency)}
              </span>
            ))}
          </div>
        )}

        {(b.exclusions ?? []).length > 0 && (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>Excluded from the fee: {b.exclusions!.join(", ")}</div>
        )}

        {/* the headline: what's billable, what's landed, what's left */}
        {/* Money only becomes DUE once it has been invoiced. Contract value not
            yet billed is future work, not a debt — keeping them apart is the
            difference between "they owe 857,500" and "they owe 31,057,500". */}
        <div className="row wrap" style={{ gap: 0, marginTop: 16, border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden" }}>
          <Headline label="Contract value" value={money(totals?.billable ?? 0, b.currency)} sub="the whole engagement" />
          <Headline label="Invoiced" value={money(totals?.invoiced ?? 0, b.currency)} sub={`${Math.round((totals?.pctInvoiced ?? 0) * 100)}% billed${b.tax?.enabled ? " · ex-VAT" : ""}`} />
          <Headline label="Paid" value={money(totals?.paid ?? 0, b.currency)} sub="received" tone="ok" />
          <Headline label="Balance due" value={money(totals?.dueNow ?? 0, b.currency)} sub={b.tax?.enabled ? "fee, ex-VAT" : "invoiced, not yet paid"} tone="accent" />
        </div>

        <div style={{ marginTop: 14 }}><Meter value={Math.round((totals?.pctInvoiced ?? 0) * 100)} /></div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
          {money(totals?.remainingToBill ?? 0, b.currency)} of the contract still to bill — not due until invoiced
          {(totals?.deferred ?? 0) > 0 && <> · <span style={{ color: "var(--warn)" }}>{money(totals!.deferred, b.currency)} earned but deferred</span></>}
          {mismatch && <> · <span style={{ color: "var(--warn)" }}>milestones total {money(totals?.allocated ?? 0, b.currency)} — {money(Math.abs(totals?.unallocated ?? 0), b.currency)} {(totals?.unallocated ?? 0) > 0 ? "unallocated" : "over"}</span></>}
        </div>

        {/* the standing, straight to the client — WhatsApp or email, with the link and the summary */}
        {canEdit && (
          <div className="row between wrap" style={{ gap: 10, marginTop: 14, padding: "12px 14px", borderRadius: 12, border: "1px solid color-mix(in srgb, var(--accent) 35%, var(--line))", background: "color-mix(in srgb, var(--accent) 6%, transparent)" }}>
            <span style={{ minWidth: 0 }}>
              <span className="kicker" style={{ color: "var(--accent-ink)" }}>Share this standing with the client</span>
              <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2 }}>Contract value, invoiced, paid, balance due, each open invoice and the portal link — by WhatsApp or email.</span>
            </span>
            <ShareStanding p={p} by={STUDIO.principal} />
          </div>
        )}

        {/* VAT is a pass-through — shown apart so it never inflates the fee figures */}
        {totals && b.tax?.enabled && totals.taxInvoiced > 0 && (
          <div className="row between wrap" style={{ marginTop: 12, padding: "10px 13px", borderRadius: 10, background: "var(--paper-2)", gap: 10, fontSize: 12.5 }}>
            <span className="row" style={{ gap: 8 }}><b>{b.tax.label || "VAT"} ({(b.tax.rate * 100).toFixed(b.tax.rate * 100 % 1 ? 1 : 0)}%)</b><span className="dim">— added on top, not part of the fee above</span></span>
            <span className="row" style={{ gap: 16 }}>
              <span><span className="dim">invoiced</span> <span className="mono">{money(totals.taxInvoiced, b.currency)}</span></span>
              <span><span className="dim">paid</span> <span className="mono" style={{ color: "var(--ok)" }}>{money(totals.taxPaid, b.currency)}</span></span>
              <span><span className="dim">total to collect</span> <span className="mono" style={{ fontWeight: 600 }}>{money(totals.dueGross, b.currency)}</span></span>
            </span>
          </div>
        )}

        <div className="row wrap" style={{ gap: 18, marginTop: 14 }}>
          {b.retentionPct !== undefined && <Fact label="Retention" value={`${b.retentionPct}%`} />}
          {b.paymentTermsDays !== undefined && <Fact label="Payment terms" value={`${b.paymentTermsDays} days`} />}
          {b.tax?.enabled && b.tax.rate > 0 && <Fact label={b.tax.label || "VAT"} value={`${(b.tax.rate * 100).toFixed(b.tax.rate * 100 % 1 ? 1 : 0)}%`} />}
        </div>

        {canEdit && <TaxToggle p={p} />}
      </div>

      {/* stage valuations lead when they supersede the milestone list */}
      {superseded && (b.valuations ?? []).map((v) => v.monthly
        ? <MonthlyDrawCard key={v.id} p={p} v={v} canEdit={canEdit} />
        : <ValuationCard key={v.id} p={p} v={v} canEdit={canEdit} />)}

      {/* the milestone schedule — hidden once a valuation bills the project */}
      {!superseded && (
      <div className="card reveal" style={{ overflow: "hidden" }}>
        <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
          <span>
            <span className="kicker">{superseded ? "Engagement schedule — superseded" : "Schedule"}</span>
            {superseded && <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
              Kept for reference. Billing runs off the stage valuation above, so these no longer raise invoices.
            </span>}
          </span>
          {canEdit && !superseded && <button className="btn ghost sm" onClick={() => setAddOpen(true)}><Icon.plus width={13} />Add a milestone</button>}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
            <thead>
              <tr>{["", "Milestone", "%", "Amount", "Due", "Status", ""].map((h, i) => (
                <th key={i} style={{ textAlign: i === 2 || i === 3 ? "right" : "left", padding: "10px 12px", borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {b.milestones.map((m) => {
                const inv = p.invoices.find((i) => i.id === m.invoiceId);
                const isPaid = inv?.status === "paid";
                const late = inv && !isPaid && daysUntil(inv.dueOn) < 0;
                return (
                  <tr key={m.id} style={{ borderBottom: "1px solid var(--line)" }}>
                    <td className="mono dim" style={{ padding: "11px 12px", fontSize: 11 }}>{m.ref}</td>
                    <td style={{ padding: "11px 12px" }}>
                      <div style={{ fontWeight: 500 }}>{m.label}</div>
                      {m.basis && <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{m.basis}</div>}
                      {inv && <div className="dim mono" style={{ fontSize: 11, marginTop: 2 }}>{inv.ref} · due {fmtDate(inv.dueOn)}</div>}
                    </td>
                    <td className="mono" style={{ padding: "11px 12px", textAlign: "right" }}>{m.pct !== undefined ? `${(m.pct * 100).toFixed(m.pct * 100 % 1 ? 1 : 0)}%` : "—"}</td>
                    <td className="mono" style={{ padding: "11px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {canEdit && m.status === "scheduled" ? (
                        <input className="input mono" value={m.amount} onChange={(e) => actions.updateMilestone(p.id, m.id, { amount: Number(e.target.value) || 0 })}
                          style={{ width: 108, padding: "5px 8px", fontSize: 12.5, textAlign: "right" }} />
                      ) : money(m.amount, b.currency)}
                    </td>
                    <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}>
                      {canEdit && m.status === "scheduled" ? (
                        <input className="input" type="date" value={m.dueOn ?? ""} onChange={(e) => actions.updateMilestone(p.id, m.id, { dueOn: e.target.value })}
                          style={{ width: 140, padding: "5px 8px", fontSize: 12 }} />
                      ) : m.dueOn ? fmtDate(m.dueOn) : <span className="dim">—</span>}
                    </td>
                    <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}>
                      {isPaid ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Paid</span>
                        : m.status === "invoiced" ? <span className={"badge " + (late ? "warn" : "info")} style={{ fontSize: 9 }}>{late ? "Overdue" : "Invoiced"}</span>
                        : <span className="badge" style={{ fontSize: 9 }}>Scheduled</span>}
                    </td>
                    <td style={{ padding: "11px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="chip" style={{ fontSize: 11, marginRight: 6 }} onClick={() => setOpenRow(openRow === m.id ? null : m.id)}>
                        {openRow === m.id ? "Hide" : "Breakdown"}{m.items?.length ? ` · ${m.items.length}` : ""}
                      </button>
                      {canEdit && !superseded && m.status === "scheduled" && (
                        <>
                          <button className="btn accent sm" onClick={() => actions.issueInvoiceForMilestone(p.id, m.id)} disabled={m.amount <= 0}>
                            <Icon.send width={12} />Invoice
                          </button>
                          <button className="chip" style={{ fontSize: 11, marginLeft: 6, color: "var(--danger)" }} onClick={() => actions.removeMilestone(p.id, m.id)}>✕</button>
                        </>
                      )}
                      {canEdit && inv && !isPaid && (
                        <button className="btn soft sm" onClick={() => setPayFor(inv.id)}><Icon.money width={12} />Record payment</button>
                      )}
                    </td>
                  </tr>
                );
              }).flatMap((row, i) => {
                const m = b.milestones[i];
                if (openRow !== m.id) return [row];
                return [row, <MilestoneBreakdown key={m.id + "-bd"} p={p} m={m} canEdit={canEdit} />];
              })}
              {b.milestones.length === 0 && (
                <tr><td colSpan={7}><div className="dim" style={{ padding: 22, textAlign: "center" }}>No milestones on the schedule yet.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {!superseded && (b.valuations ?? []).map((v) => v.monthly
        ? <MonthlyDrawCard key={v.id} p={p} v={v} canEdit={canEdit} />
        : <ValuationCard key={v.id} p={p} v={v} canEdit={canEdit} />)}

      {/* what falls outside the fee, and what's included before it's charged */}
      {(b.additionalServices ?? []).length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">Additional services &amp; rates</span>
            <span className="dim" style={{ fontSize: 11.5 }}>charged outside the {money(b.contractSum, b.currency)} fee</span>
          </div>
          {b.additionalServices!.map((s, i) => (
            <div key={i} className="row between wrap" style={{ padding: "11px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.label}</span>
                {(s.included !== undefined || s.note) && (
                  <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                    {s.included !== undefined ? `${s.included} included` : ""}{s.included !== undefined && s.note ? " · " : ""}{s.note ?? ""}
                  </span>
                )}
              </span>
              <span className="mono" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                {s.rate > 0 ? money(s.rate, b.currency) : "—"}
                {s.rateMax && s.rateMax > s.rate ? `–${money(s.rateMax, b.currency)}` : ""}
                {s.unit ? <span className="dim"> / {s.unit}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}

      {readOpen && <ReadContractModal p={p} onClose={() => setReadOpen(false)} busy={reading} setBusy={setReading} />}
      {addOpen && <AddMilestoneModal p={p} onClose={() => setAddOpen(false)} />}
      {payFor && <RecordPaymentModal p={p} invoiceId={payFor} onClose={() => setPayFor(null)} />}
    </div>
  );
}

/* A hosted online-payment link (Stripe, bank checkout) clients pay invoices
   through — card details are entered on the provider's page, never here. */
function PayLinkRow({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const b = p.billing!;
  const [url, setUrl] = useState(b.payUrl ?? "");
  return (
    <div className="panel" style={{ padding: "11px 14px", marginTop: 12 }}>
      <div className="row" style={{ gap: 8, marginBottom: url || canEdit ? 8 : 0 }}>
        <Icon.link width={14} style={{ color: "var(--accent-ink)" }} />
        <b style={{ fontSize: 13 }}>Online payment link</b>
        {b.payUrl && <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />live</span>}
      </div>
      {canEdit ? (
        <div className="row" style={{ gap: 8 }}>
          <input className="input mono" style={{ flex: 1, fontSize: 12 }} value={url} onChange={(e) => setUrl(e.target.value)} onBlur={() => { if (url.trim() !== (b.payUrl ?? "")) actions.setBillingPayUrl(p.id, url); }} placeholder="https://buy.stripe.com/… or your bank checkout link" />
          {b.payUrl && <button className="btn ghost sm" onClick={() => { setUrl(""); actions.setBillingPayUrl(p.id, ""); }}>Clear</button>}
        </div>
      ) : b.payUrl ? <a className="mono" style={{ fontSize: 12 }} href={b.payUrl} target="_blank" rel="noreferrer">{b.payUrl}</a> : <span className="dim" style={{ fontSize: 12 }}>Not set.</span>}
      <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>Paste a hosted checkout link (Stripe Payment Link, bank, etc.). The client sees a “Pay online” button — card entry happens on the provider's secure page.</div>
    </div>
  );
}

/* A second currency to report costs in — overseas supply, foreign consultants.
   The rate is a snapshot: each invoice keeps the rate it was raised at, so a
   later change never re-values what was already billed. */
function FxRow({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const b = p.billing!;
  const [ccy, setCcy] = useState(b.fx?.costCurrency ?? "");
  const [rate, setRate] = useState(String(b.fx?.rate ?? ""));
  const on = !!b.fx;
  return (
    <div className="panel" style={{ padding: "11px 14px", marginTop: 12 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <span className="row" style={{ gap: 8 }}>
          <Icon.swap width={14} style={{ color: "var(--accent-ink)" }} />
          <b style={{ fontSize: 13 }}>Second currency{on ? ` — 1 ${b.currency} = ${b.fx!.rate} ${b.fx!.costCurrency}` : ""}</b>
          {on && <span className="dim" style={{ fontSize: 11 }}>set {fmtDate(b.fx!.setOn)}</span>}
        </span>
        {canEdit && on && <button className="chip" style={{ fontSize: 10.5 }} onClick={() => { setCcy(""); setRate(""); actions.setFx(p.id, "", 0); }}>Remove</button>}
      </div>
      {canEdit && (
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
          <span className="dim" style={{ fontSize: 12 }}>1 {b.currency} =</span>
          <input className="input mono" style={{ width: 110 }} type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="209" />
          <input className="input mono" style={{ width: 90 }} value={ccy} onChange={(e) => setCcy(e.target.value.toUpperCase())} placeholder="GYD" maxLength={4} />
          <button className="btn ghost sm" disabled={!ccy.trim() || !(Number(rate) > 0)} onClick={() => actions.setFx(p.id, ccy, Number(rate))}>Set rate</button>
        </div>
      )}
      <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>The fee is always billed in {b.currency}. This shows a second figure alongside it for costs you settle elsewhere — each invoice keeps the rate it was raised at.</div>
    </div>
  );
}

/* Charges that raise their own invoice on a cycle — a monthly retainer, or the
   construction-administration phase billed month by month. */
function RetainersRow({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const b = p.billing!;
  const list = b.retainers ?? [];
  const [add, setAdd] = useState(false);
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [cycle, setCycle] = useState<"monthly" | "quarterly">("monthly");
  const [startOn, setStartOn] = useState(new Date().toISOString().slice(0, 10));

  return (
    <div className="panel" style={{ padding: "11px 14px", marginTop: 12 }}>
      <div className="row between wrap" style={{ gap: 8, marginBottom: list.length || add ? 8 : 0 }}>
        <span className="row" style={{ gap: 8 }}><Icon.calendar width={14} style={{ color: "var(--accent-ink)" }} /><b style={{ fontSize: 13 }}>Recurring charges</b>{list.length > 0 && <span className="dim" style={{ fontSize: 11.5 }}>{list.filter((r) => r.active).length} active</span>}</span>
        {canEdit && !add && <button className="chip" style={{ fontSize: 10.5 }} onClick={() => setAdd(true)}>Add</button>}
      </div>
      {list.map((r) => (
        <div key={r.id} className="row between wrap" style={{ gap: 8, padding: "6px 0", borderTop: "1px solid var(--line)" }}>
          <span className="row" style={{ gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 13 }}>{r.label}</span>
            <span className="dim" style={{ fontSize: 11.5 }}>{r.cycle} · from {fmtDate(r.startOn)}{r.lastRunOn ? ` · last billed ${fmtDate(r.lastRunOn)}` : ""}</span>
            {!r.active && <span className="badge" style={{ fontSize: 8.5 }}>paused</span>}
          </span>
          <span className="row" style={{ gap: 8, flexShrink: 0 }}>
            <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{money(r.amount, b.currency)}</span>
            {canEdit && <>
              <button className="chip" style={{ fontSize: 10.5 }} onClick={() => actions.runRetainer(p.id, r.id, STUDIO.principal)}>Bill now</button>
              <button className="chip" style={{ fontSize: 10.5 }} onClick={() => actions.setRetainerActive(p.id, r.id, !r.active)}>{r.active ? "Pause" : "Resume"}</button>
              <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => { if (confirm(`Remove “${r.label}”?`)) actions.removeRetainer(p.id, r.id); }}><Icon.close width={12} /></button>
            </>}
          </span>
        </div>
      ))}
      {add && canEdit && (
        <div className="row wrap" style={{ gap: 8, marginTop: 8, alignItems: "flex-end" }}>
          <div className="field" style={{ flex: "1 1 150px", margin: 0 }}><label style={{ fontSize: 11 }}>Label</label><input className="input" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Construction administration" /></div>
          <div className="field" style={{ width: 110, margin: 0 }}><label style={{ fontSize: 11 }}>Amount</label><input className="input mono" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div className="field" style={{ width: 110, margin: 0 }}><label style={{ fontSize: 11 }}>Cycle</label><select className="select" value={cycle} onChange={(e) => setCycle(e.target.value as any)}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option></select></div>
          <div className="field" style={{ width: 140, margin: 0 }}><label style={{ fontSize: 11 }}>Starts</label><input className="input" type="date" value={startOn} onChange={(e) => setStartOn(e.target.value)} /></div>
          <button className="btn accent sm" disabled={!label.trim() || !(Number(amount) > 0)} onClick={() => { actions.addRetainer(p.id, { label, amount: Number(amount), cycle, startOn }); setLabel(""); setAmount(""); setAdd(false); }}>Save</button>
          <button className="btn ghost sm" onClick={() => setAdd(false)}>Cancel</button>
        </div>
      )}
      {list.length === 0 && !add && <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>For a monthly retainer or a CA phase billed month by month — raise each cycle's invoice with one click.</div>}
    </div>
  );
}

/* Advance retainer — an up-front slice of the design fee (default 15%), taken on
   appointment and credited back as the stages are billed. */
function RetainerPanel({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const b = p.billing!;
  const r = b.retainer;
  const [pct, setPct] = useState(String(r?.pct ?? 15));
  const invoiced = r?.invoiceId ? p.invoices.find((i) => i.id === r.invoiceId) : undefined;

  if (!r) {
    if (!canEdit) return null;
    return (
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn ghost sm" onClick={() => actions.setRetainer(p.id, true, 15)} title="Take an up-front retainer, 15% of the design fee">
          <Icon.plus width={13} />Add advance retainer (15% of fee)
        </button>
      </div>
    );
  }
  return (
    <div className="panel" style={{ padding: "11px 14px", marginTop: 12, background: "color-mix(in srgb, var(--accent) 7%, var(--card))", borderColor: "color-mix(in srgb, var(--accent) 30%, var(--line))" }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <span className="row" style={{ gap: 8 }}>
          <Icon.spark width={14} style={{ color: "var(--accent-ink)" }} />
          <b style={{ fontSize: 13 }}>Advance retainer — {r.pct}% of fee = {money(r.amount, b.currency)}</b>
        </span>
        <span className="row" style={{ gap: 8 }}>
          {invoiced
            ? <span className="badge ok" style={{ fontSize: 9.5 }}><Icon.check width={9} />Invoiced · {invoiced.ref}</span>
            : canEdit && <button className="btn accent sm" onClick={() => actions.issueRetainerInvoice(p.id)}><Icon.send width={12} />Invoice retainer</button>}
          {canEdit && !invoiced && <button className="chip" style={{ fontSize: 10.5 }} onClick={() => { if (confirm("Remove the advance retainer?")) actions.setRetainer(p.id, false); }}>Remove</button>}
        </span>
      </div>
      <div className="dim" style={{ fontSize: 12, marginTop: 5, lineHeight: 1.5 }}>
        Taken up-front on appointment{invoiced ? "" : " — invoice it before the stage milestones run"}; credited back as each design stage is billed.
      </div>
      {canEdit && !invoiced && (
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
          <span className="dim" style={{ fontSize: 12 }}>Rate</span>
          <input className="input mono" type="number" value={pct} onChange={(e) => setPct(e.target.value)}
            onBlur={() => { const n = Number(pct); if (n > 0 && n <= 100) actions.setRetainer(p.id, true, n); else setPct(String(r.pct)); }}
            style={{ width: 76 }} />
          <span className="dim" style={{ fontSize: 12 }}>% of {money(b.contractSum, b.currency)} fee</span>
        </div>
      )}
    </div>
  );
}

/** One of the three headline figures — billable · paid · balance. */
/* VAT / tax toggle. Applies to invoices raised AFTER it's turned on — issued
   invoices keep whatever rate they were billed at. */
function TaxToggle({ p }: { p: Project }) {
  const b = p.billing!;
  const on = !!b.tax?.enabled;
  const [rate, setRate] = useState(String(((b.tax?.rate ?? 0.14) * 100)));
  const [label, setLabel] = useState(b.tax?.label ?? "VAT");
  return (
    <div className="panel" style={{ padding: "11px 14px", marginTop: 14 }}>
      <div className="row between wrap" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 9, minWidth: 0 }}>
          <button role="switch" aria-checked={on} onClick={() => actions.setBillingTax(p.id, { enabled: !on, label, rate: (Number(rate) || 0) / 100 })}
            style={{ width: 40, height: 23, borderRadius: 999, border: "none", cursor: "pointer", background: on ? "var(--accent)" : "var(--line-2)", position: "relative", flexShrink: 0, transition: "background .15s" }}>
            <span style={{ position: "absolute", top: 2, left: on ? 19 : 2, width: 19, height: 19, borderRadius: 999, background: "#fff", transition: "left .15s" }} />
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>Charge {label || "VAT"} on invoices</div>
            <div className="dim" style={{ fontSize: 11.5 }}>{on ? `Added on top of each invoice at ${(((b.tax?.rate ?? 0) * 100)).toFixed(b.tax?.rate && b.tax.rate * 100 % 1 ? 1 : 0)}%.` : "Off — invoices are billed with no tax."}</div>
          </div>
        </div>
        <div className="row" style={{ gap: 6, flexShrink: 0 }}>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => on && actions.setBillingTax(p.id, { enabled: true, label, rate: (Number(rate) || 0) / 100 })} style={{ width: 72, fontSize: 12.5, padding: "5px 8px" }} title="Tax label" />
          <div className="row" style={{ gap: 3, alignItems: "center" }}>
            <input className="input mono" value={rate} onChange={(e) => setRate(e.target.value)} onBlur={() => on && actions.setBillingTax(p.id, { enabled: true, label, rate: (Number(rate) || 0) / 100 })} style={{ width: 56, textAlign: "right", fontSize: 12.5, padding: "5px 8px" }} title="Rate %" />
            <span className="dim" style={{ fontSize: 12.5 }}>%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Headline({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "ok" | "accent" }) {
  return (
    <div style={{ flex: "1 1 160px", padding: "14px 16px", borderRight: "1px solid var(--line)", background: tone === "accent" ? "color-mix(in srgb,var(--accent) 6%,transparent)" : "transparent" }}>
      <div className="kicker" style={{ fontSize: 8.5 }}>{label}</div>
      <div className="serif" style={{ fontSize: 24, marginTop: 4, color: tone === "ok" ? "var(--ok)" : tone === "accent" ? "var(--accent-ink)" : "var(--ink)" }}>{value}</div>
      {sub && <div className="dim" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="kicker" style={{ fontSize: 8.5 }}>{label}</div>
      <div className="mono" style={{ fontSize: 14, marginTop: 3 }}>{value}</div>
    </div>
  );
}

/** Paste the payment clauses or drop the whole contract — the AI pulls the
    structure out and we turn it into a schedule. */
function ReadContractModal({ p, onClose, busy, setBusy }: { p: Project; onClose: () => void; busy: boolean; setBusy: (b: boolean) => void }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState<{ name: string; data: string } | null>(null);
  const [err, setErr] = useState("");

  async function pick(f?: File) {
    if (!f) return;
    setErr("");
    try { setFile({ name: f.name, data: await rawDataUrl(f) }); }
    catch { setErr("Couldn't read that file — try a PDF."); }
  }

  async function run() {
    if (!text.trim() && !file) return;
    setBusy(true); setErr("");
    const read = await readPaymentStructure({
      text: text.trim() || undefined, fileData: file?.data,
      projectName: p.name, currency: p.currency, contractSum: p.budget,
    });
    setBusy(false);
    if (!read.ok || !(read.milestones ?? []).length) {
      setErr(read.reason === "no-key" ? "AI isn't configured on the worker yet — build from the fee stages instead."
        : read.reason === "nothing-to-read" ? "Paste the payment clauses or attach the contract first."
        : "Couldn't find a payment structure in that. Try pasting just the payment clauses.");
      return;
    }
    actions.setPaymentSchedule(p.id, scheduleFromRead(read, { contractSum: p.budget, currency: p.currency }, file?.name ?? "Read from pasted contract text"));
    onClose();
  }

  return (
    <Modal open onClose={onClose} title="Read the payment structure" wide>
      <div className="col" style={{ gap: 13 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          Paste the payment clauses, or attach the signed contract. Nothing is invented — figures and dates come out
          only where the document actually states them, and anything missing is left for you to fill.
        </p>
        <div className="field"><label>Payment clauses</label>
          <textarea className="input" rows={7} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={"e.g. 20% on signing of this Agreement; 25% on completion of Schematic Design; 30% on issue of Construction Documents; 25% on practical completion. Retention 5%. Invoices payable within 14 days."} />
        </div>
        <div className="row wrap" style={{ gap: 10, alignItems: "center" }}>
          <label className="btn soft sm" style={{ cursor: "pointer" }}>
            <Icon.download width={14} style={{ transform: "rotate(180deg)" }} />{file ? "Replace contract" : "Attach the contract (PDF)"}
            <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => void pick(e.target.files?.[0] ?? undefined)} />
          </label>
          {file && <span className="badge ok" style={{ fontSize: 9.5 }}><Icon.check width={10} />{file.name}</span>}
        </div>
        {err && <p style={{ color: "var(--warn)", fontSize: 12.5 }}>{err}</p>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" onClick={() => void run()} disabled={busy || (!text.trim() && !file)}>
            <Icon.spark width={15} />{busy ? "Reading…" : "Read & build the schedule"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddMilestoneModal({ p, onClose }: { p: Project; onClose: () => void }) {
  const b = p.billing!;
  const [label, setLabel] = useState("");
  const [pct, setPct] = useState("");
  const [amount, setAmount] = useState("");
  const [dueOn, setDueOn] = useState("");
  const derived = pct ? Math.round(b.contractSum * (Number(pct) / 100)) : Number(amount) || 0;

  return (
    <Modal open onClose={onClose} title="Add a payment milestone">
      <div className="col" style={{ gap: 12 }}>
        <div className="field"><label>Milestone</label>
          <input className="input" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. On practical completion" />
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>% of contract sum</label>
            <input className="input mono" value={pct} onChange={(e) => { setPct(e.target.value); setAmount(""); }} placeholder="25" />
          </div>
          <div className="field" style={{ flex: 1 }}><label>or a fixed amount</label>
            <input className="input mono" value={amount} onChange={(e) => { setAmount(e.target.value); setPct(""); }} placeholder="12000" />
          </div>
        </div>
        <div className="field"><label>Due <span className="dim">(optional)</span></label>
          <input className="input" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </div>
        <div className="dim" style={{ fontSize: 12.5 }}>This milestone will bill {money(derived, b.currency)}.</div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!label.trim() || derived <= 0}
            onClick={() => { actions.addMilestone(p.id, { label, pct: pct ? Number(pct) / 100 : undefined, amount: derived, dueOn }); onClose(); }}>
            <Icon.check width={15} />Add
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** What a milestone covers — the breakdown the client sees on the payment link. */
function MilestoneBreakdown({ p, m, canEdit }: { p: Project; m: PaymentMilestone; canEdit: boolean }) {
  const b = p.billing!;
  const [adding, setAdding] = useState("");
  const [copied, setCopied] = useState(false);
  const items = m.items ?? [];
  const itemised = items.reduce((s, i) => s + (i.amount ?? 0), 0);
  const fmt = (n: number) => money(n, b.currency);

  function add() {
    if (!adding.trim()) return;
    actions.setMilestoneItems(p.id, m.id, [...items, { label: adding }]);
    setAdding("");
  }
  function copyLink() {
    navigator.clipboard?.writeText(paymentLinkText(p, m, fmt)).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  }

  return (
    <tr>
      <td colSpan={7} style={{ padding: 0, background: "var(--paper-2)", borderBottom: "1px solid var(--line)" }}>
        <div style={{ padding: "14px 18px" }}>
          <div className="row between wrap" style={{ gap: 10, marginBottom: 10 }}>
            <span className="kicker" style={{ fontSize: 9 }}>What this payment covers</span>
            {canEdit && (
              <div className="row wrap" style={{ gap: 6 }}>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.suggestMilestoneItems(p.id, m.id)}>
                  <Icon.spark width={11} />Fill from the drawings
                </button>
                <button className="chip" style={{ fontSize: 11 }} onClick={copyLink}>{copied ? "Copied ✓" : "Copy payment link"}</button>
                <button className="chip" style={{ fontSize: 11, color: "#1fa855" }}
                  onClick={() => openWhatsApp(paymentLinkText(p, m, fmt), p.client.phone)}>
                  <Icon.whatsapp width={11} />Send
                </button>
              </div>
            )}
          </div>

          {items.length === 0 ? (
            <div className="dim" style={{ fontSize: 12.5 }}>
              Nothing itemised yet — add what this covers so the client can see exactly what they're paying for.
            </div>
          ) : (
            <div className="col" style={{ gap: 0 }}>
              {items.map((i, n) => (
                <div key={i.id} className="row between" style={{ padding: "7px 0", borderTop: n ? "1px solid var(--line)" : "none", gap: 10 }}>
                  <span style={{ fontSize: 12.5, minWidth: 0 }}>
                    {i.label}{i.note ? <span className="dim"> · {i.note}</span> : null}
                  </span>
                  <span className="row" style={{ gap: 8, flexShrink: 0 }}>
                    {i.amount ? <span className="mono" style={{ fontSize: 12 }}>{fmt(i.amount)}</span> : null}
                    {canEdit && (
                      <button className="chip" style={{ fontSize: 10, color: "var(--danger)" }}
                        onClick={() => actions.setMilestoneItems(p.id, m.id, items.filter((x) => x.id !== i.id))}>✕</button>
                    )}
                  </span>
                </div>
              ))}
              {itemised > 0 && (
                <div className="row between" style={{ padding: "8px 0 0", borderTop: "1px solid var(--line-2)", marginTop: 4 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>Itemised</span>
                  <span className="mono" style={{ fontSize: 12 }}>{fmt(itemised)}{Math.abs(itemised - m.amount) > 1 ? <span className="dim"> of {fmt(m.amount)}</span> : null}</span>
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <input className="input" value={adding} onChange={(e) => setAdding(e.target.value)}
                placeholder="Add what's covered — e.g. Schematic floor plans, elevations and sections"
                style={{ fontSize: 12.5 }} onKeyDown={(e) => e.key === "Enter" && add()} />
              <button className="btn soft sm" onClick={add} disabled={!adding.trim()}><Icon.plus width={13} /></button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Money in against an invoice — part payments welcome. */
export function RecordPaymentModal({ p, invoiceId, onClose }: { p: Project; invoiceId: string; onClose: () => void }) {
  const inv = p.invoices.find((i) => i.id === invoiceId)!;
  const rec = invoiceReceived(p, invoiceId);
  const [amount, setAmount] = useState(String(rec?.balance ?? inv.amount));
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const n = Number(amount) || 0;
  const settles = n >= (rec?.balance ?? inv.amount) - 0.5;

  return (
    <Modal open onClose={onClose} title={`Record a payment — ${inv.ref}`}>
      <div className="col" style={{ gap: 12 }}>
        <div className="panel" style={{ padding: "10px 13px", fontSize: 12.5 }}>
          <div className="row between"><span className="dim">Invoiced</span><span className="mono">{money(inv.amount, p.currency)}</span></div>
          {rec && rec.received > 0 && <div className="row between" style={{ marginTop: 4 }}><span className="dim">Already received</span><span className="mono">{money(rec.received, p.currency)}</span></div>}
          <div className="row between" style={{ marginTop: 4 }}><span className="dim">Outstanding</span><span className="mono">{money(rec?.balance ?? inv.amount, p.currency)}</span></div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>Amount received</label>
            <input className="input mono" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}><label>Date</label>
            <input className="input" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
          </div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>Method</label>
            <select className="select" value={method} onChange={(e) => setMethod(e.target.value)}>
              {["Bank transfer", "Cheque", "Card", "Cash", "Other"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}><label>Reference</label>
            <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transfer ref" />
          </div>
        </div>
        <div className="dim" style={{ fontSize: 12 }}>
          {n <= 0 ? "Enter the amount received."
            : settles ? `This settles ${inv.ref} and marks the milestone paid.`
            : `Part payment — ${money((rec?.balance ?? inv.amount) - n, p.currency)} will remain outstanding.`}
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={n <= 0}
            onClick={() => { actions.recordInvoicePayment(p.id, invoiceId, { amount: n, on, method, reference }); onClose(); }}>
            <Icon.check width={15} />Record {money(n, p.currency)}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** A stage certified the way the agreed sheet does it: value × % complete,
    the deferral held off the earned amount, and the net payable now. */
function ValuationCard({ p, v, canEdit }: { p: Project; v: StageValuation; canEdit: boolean }) {
  const b = p.billing!;
  const t = valuationTotals(v);
  const inv = p.invoices.find((i) => i.id === v.invoiceId);
  const rec = invoiceReceived(p, v.invoiceId);
  const [payOpen, setPayOpen] = useState(false);
  const cell: React.CSSProperties = { padding: "9px 12px", textAlign: "right", whiteSpace: "nowrap" };

  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <div>
          <span className="kicker">{v.stage}</span>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>
            Certified by percentage complete · {Math.round(v.deferredPct * 100)}% deferred on each line
          </div>
        </div>
        {inv
          ? (
            <span className="row wrap" style={{ gap: 8 }}>
              <span className={"badge " + (inv.status === "paid" ? "ok" : "info")} style={{ fontSize: 9.5 }}>
                <Icon.check width={10} />{inv.status === "paid" ? "Settled" : "Certified"} · {inv.ref}
              </span>
              {canEdit && inv.status !== "paid" && (
                <button className="btn accent sm" onClick={() => setPayOpen(true)}>
                  <Icon.money width={13} />Receive payment
                </button>
              )}
            </span>
          )
          : canEdit && (
            <button className="btn accent sm" disabled={t.total <= 0}
              onClick={() => actions.certifyValuation(p.id, v.id, t.total, t.deferred)}>
              <Icon.send width={13} />Certify &amp; invoice {money(t.total, b.currency)}
            </button>
          )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 660 }}>
          <thead>
            <tr>{["Scope", "% Complete", "Value", "Sub-Total", "Deferred", "Total"].map((h, i) => (
              <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "10px 12px", borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--line)" }}>
                <td style={{ padding: "9px 12px", fontWeight: 500 }}>{r.scope}</td>
                <td style={cell}>
                  {canEdit && !inv ? (
                    <span className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                      <input className="input mono" value={Math.round(r.pctComplete * 100)}
                        onChange={(e) => actions.setValuationProgress(p.id, v.id, r.id, (Number(e.target.value) || 0) / 100)}
                        style={{ width: 58, padding: "4px 6px", fontSize: 12.5, textAlign: "right" }} />
                      <span className="dim">%</span>
                    </span>
                  ) : <span className="mono">{Math.round(r.pctComplete * 100)}%</span>}
                </td>
                <td style={{ ...cell }} className="mono">
                  {canEdit && !inv ? (
                    <input className="input mono" value={r.value}
                      onChange={(e) => actions.setValuationLineValue(p.id, v.id, r.id, Number(e.target.value) || 0)}
                      style={{ width: 120, padding: "4px 6px", fontSize: 12.5, textAlign: "right" }} />
                  ) : money(r.value, b.currency)}
                </td>
                <td style={cell} className="mono">{money(r.subTotal, b.currency)}</td>
                <td style={{ ...cell, color: "var(--warn)" }} className="mono">{money(r.deferred, b.currency)}</td>
                <td style={{ ...cell, fontWeight: 600 }} className="mono">{money(r.total, b.currency)}</td>
              </tr>
            ))}
            <tr style={{ background: "var(--paper-2)" }}>
              <td style={{ padding: "11px 12px", fontWeight: 700 }}>TOTAL</td>
              <td style={cell} />
              <td style={cell} className="mono">{money(t.value, b.currency)}</td>
              <td style={cell} className="mono">{money(t.subTotal, b.currency)}</td>
              <td style={{ ...cell, color: "var(--warn)" }} className="mono">{money(t.deferred, b.currency)}</td>
              <td style={{ ...cell, fontWeight: 700 }} className="mono">{money(t.total, b.currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="dim" style={{ fontSize: 11.5, padding: "10px 18px" }}>
        {money(t.subTotal, b.currency)} earned · {money(t.deferred, b.currency)} held as the deferred portion ·{" "}
        <b style={{ color: "var(--ink)" }}>{money(t.total, b.currency)} payable now</b>
        {t.value - t.subTotal > 0 ? ` · ${money(t.value - t.subTotal, b.currency)} still to earn on this stage` : ""}
        {rec && rec.received > 0 && (
          <> · <b style={{ color: "var(--ok)" }}>{money(rec.received, b.currency)} received</b>
            {rec.balance > 0 ? ` · ${money(rec.balance, b.currency)} still owed` : " · settled"}</>
        )}
      </div>
      {payOpen && inv && <RecordPaymentModal p={p} invoiceId={inv.id} onClose={() => setPayOpen(false)} />}
    </div>
  );
}

/** A stage drawn monthly — the remaining balance divided by the months still
    to run, so an early or late month simply re-spreads what's left. */
function MonthlyDrawCard({ p, v, canEdit }: { p: Project; v: StageValuation; canEdit: boolean }) {
  const b = p.billing!;
  const d = monthlyDraw(v);
  const [payFor, setPayFor] = useState<string | null>(null);
  if (!d) return null;
  const done = d.remainingMonths === 0 || d.balance <= 0;
  const drawInvoices = p.invoices.filter((i) => i.title.startsWith(`${v.stage} — month`));

  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <div>
          <span className="kicker">{v.stage}</span>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>
            Billed monthly — the balance spread over the months still to run
            {v.deferredPct > 0 ? ` · ${Math.round(v.deferredPct * 100)}% deferred on each draw` : ""}
          </div>
        </div>
        {canEdit && !done && (
          <button className="btn accent sm" onClick={() => actions.billMonthlyDraw(p.id, v.id, d.gross, d.net, d.deferred)}>
            <Icon.send width={13} />Bill month {d.monthsDrawn + 1} · {money(d.net, b.currency)}
          </button>
        )}
        {done && <span className="badge ok" style={{ fontSize: 9.5 }}><Icon.check width={10} />Fully drawn</span>}
      </div>

      <div className="row wrap" style={{ gap: 0 }}>
        <Cell label="Stage value" value={money(d.value, b.currency)} />
        <Cell label="Drawn so far" value={`${d.monthsDrawn} of ${d.months} mo`} sub={money(v.monthly!.drawnAmount, b.currency)} />
        <Cell label="Balance to bill" value={money(d.balance, b.currency)} />
        <Cell label="This month" value={money(d.gross, b.currency)} sub={`${money(d.net, b.currency)} net of deferral`} accent />
      </div>

      {drawInvoices.length > 0 && (
        <div style={{ borderTop: "1px solid var(--line)" }}>
          {drawInvoices.map((inv) => {
            const rec = invoiceReceived(p, inv.id);
            return (
              <div key={inv.id} className="row between wrap" style={{ padding: "10px 18px", borderTop: "1px solid var(--line)", gap: 10 }}>
                <span style={{ fontSize: 12.5 }}>
                  {inv.title}<span className="dim mono" style={{ fontSize: 11 }}> · {inv.ref} · due {fmtDate(inv.dueOn)}</span>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <span className="mono" style={{ fontSize: 12.5 }}>{money(inv.amount, b.currency)}</span>
                  {inv.status === "paid"
                    ? <span className="badge ok" style={{ fontSize: 9 }}>Settled</span>
                    : <>
                        {rec && rec.received > 0 && <span className="dim" style={{ fontSize: 11 }}>{money(rec.received, b.currency)} in</span>}
                        {canEdit && <button className="btn soft sm" onClick={() => setPayFor(inv.id)}><Icon.money width={12} />Receive</button>}
                      </>}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {payFor && <RecordPaymentModal p={p} invoiceId={payFor} onClose={() => setPayFor(null)} />}
    </div>
  );
}

function Cell({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div style={{ flex: "1 1 150px", padding: "12px 16px", borderRight: "1px solid var(--line)", background: accent ? "color-mix(in srgb,var(--accent) 6%,transparent)" : "transparent" }}>
      <div className="kicker" style={{ fontSize: 8.5 }}>{label}</div>
      <div className="mono" style={{ fontSize: 15, marginTop: 3, color: accent ? "var(--accent-ink)" : "var(--ink)" }}>{value}</div>
      {sub && <div className="dim" style={{ fontSize: 10.5 }}>{sub}</div>}
    </div>
  );
}
